// Client-side feasibility check for the beta plan optimizer (optimizer_version 2).
//
// Mirrors the model team's Python pre-solver (feasibility_presolver /
// OnlineFeasibilityChecker). The backend models feasibility as a Linear Program:
// a non-negative invest variable per (channel, period), per-period upper bounds
//
//   invest[m, t] <= DIMINISHING_RETURNS_POINT * scale[m]
//
// (which keep single-period spend inside the range where the saturation curve was
// estimated), plus "client constraints" each bounding the SUM of invest over a
// subset of (m, t) to an interval. The total budget is one such constraint over
// all (m, t). The problem is feasible iff that constraint set admits any point.
//
// Cross-channel coupling comes from the total budget and from multi-channel
// time restrictions (a single restriction may bound the combined spend of
// several channels), and time windows may overlap freely. Solving that exactly
// needs an LP, so this check keeps only the pieces that stay cheap and sound:
//
// - Single-channel constraints with disjoint windows decompose per channel: for
//   each channel compute the achievable total-spend interval [minTotal, maxTotal]
//   under its per-period cap and its own window-sum constraints, then test the
//   budget against the summed interval (exact for that subset). Overlapping
//   windows on a channel make the closed form invalid (a routine case now that
//   the UI allows intersecting restrictions); instead of dropping the channel's
//   constraints, it falls back to relaxed-but-sound bounds — largest window
//   lower as the channel minimum, tightest window upper plus outside capacity
//   as the maximum — plus a nested-window contradiction check (a window
//   contained in another cannot require more than the outer one allows). See
//   channelSpendRange.
// - Multi-channel constraints only get necessary-condition checks: an internally
//   inverted interval (min > max) and a forced minimum above the group's total
//   window capacity are provably infeasible; everything else passes. For the
//   budget's sum-of-minimums, a group lower is added on top of totalMin only
//   when its (channels × window) box is DISJOINT from the single-channel cells
//   and from other added groups (a greedy disjoint-box packing): disjoint boxes
//   cannot share spend, so summing their lowers stays a valid lower bound.
//   Overlapping boxes are not summed (they could share spend), but each group
//   lower still individually bounds total spend, so the largest also tightens
//   the budget test. A group UPPER caps spend inside its window, so whatever
//   the members can otherwise absorb has to fit outside it; when it does not,
//   real total capacity drops by the difference. That reduction is measured
//   against the members' OWN maxima (single-channel uppers already folded in),
//   not against raw capacity, which is what lets it be subtracted straight from
//   totalMax and compose with those uppers instead of ignoring them. Reductions
//   are summed over groups with DISJOINT CHANNEL SETS (largest first): the
//   reduction bounds the total spend of the group's channels, so two groups
//   sharing a channel could count it twice and under-count capacity. The
//   ceiling is also bounded by a greedy cover of the (channel, period) grid with
//   every finite upper (see coverCeiling), which is what makes a plan split into
//   per-period or per-channel amounts add up to its real ceiling, whatever their
//   shape and whether or not its channels have a scale.
//   Interactions not modeled: a group lower squeezed by its members' own
//   uppers, and non-greedy-optimal packings of several disjoint groups.
//
// '__all__' restrictions (the backend's media=None, "all media") are expanded
// to the currently selected channels and take the normal single-/multi-channel
// path above.
//
// Conditions the backend rejects with a hard error — an empty channel
// selection ("Media columns empty") and constraints referencing channels
// outside media_columns (validate_media_in_constraints) — are also reported
// infeasible here: such a plan can never run, so blocking it is not a false
// positive.
//
// DELIBERATELY OUT OF SCOPE — deep multi-constraint interactions. This check is
// a set of closed-form, per-channel and per-group bounds; it does NOT solve the
// LP, so infeasibilities that only emerge from chaining several constraints
// together slip through (they are caught server-side). Example: with
//   Meta+OOH >= L        (group lower)
//   DC+DP+Meta+OOH <= U  (group upper)  =>  DC+DP <= U - L
//   DC+DP+Print == E     (group equality) =>  Print >= E - (U - L)
// a forced per-channel minimum on Print can exceed Print's own capacity even
// though no single constraint or disjoint-box sum is violated. Detecting this in
// general is Fourier-Motzkin / LP feasibility, which is intentionally not
// reimplemented on the client (see git history / the feasibility discussion).
// Reaching true parity would mean running the backend's maximize(0) LP here.
//
// Philosophy: FAIL-OPEN. The authoritative check still runs server-side, so a
// false "infeasible" that blocks a legitimate plan is worse than a missed
// warning. Anything we cannot evaluate confidently (missing scale, unresolved
// window, unexpected data, thrown error) resolves to feasible.

export var DIMINISHING_RETURNS_POINT = 3.0

// Media scales are no longer derived from historical spend on the front end: they
// come directly from the model report's root-level `marketing_scales` key (the same
// scales the backend uses). A channel missing from that map has an undefined scale,
// which checkFeasibility treats as an infinite per-period cap (fail-open), so it
// never causes a false infeasibility.

// Maps a UI restriction type + values to a spend interval. firstValue is the
// primary value for every type; secondValue is only used by 'between'. Units are
// absolute spend, identical to the budget. Returns null for unknown types.
function typeToInterval (type, first, second) {
  if (!isFinite(first)) first = 0
  switch (type) {
    case 'exactly': return { lower: first, upper: first }
    case 'between': return { lower: first, upper: isFinite(second) ? second : Infinity }
    case 'more_than': return { lower: first, upper: Infinity }
    case 'less_than': return { lower: 0, upper: first }
    default: return null
  }
}

// Translates the dialog's restriction state into normalized constraints
// { channels: [...], periodIndices|null, lower, upper }. `channels` has one
// entry for budget-mode and single-channel time restrictions, several for
// multi-channel time restrictions. `periods` is the model-frequency period axis
// the UI uses (timeRestrictionsSelectedPeriod) so custom-range dates resolve to
// the same indices. `selectedChannelKeys` is the list of currently selected
// channels, used to expand '__all__' restrictions. All numbers go through
// Number() for Firestore round-trip safety. Unresolvable or legacy entries are
// dropped (fail-open).
export function buildClientConstraints (params) {
  var periods = params.periods || []
  var selectedChannelKeys = params.selectedChannelKeys || []
  var constraints = []

  if (params.enableTime) {
    var timeRestrictions = params.timeRestrictionValues || []
    timeRestrictions.forEach(function (r) {
      if (!r) return
      var channelIds = Array.isArray(r.channelId) ? r.channelId : [r.channelId]
      if (channelIds.indexOf('__all__') !== -1) {
        // '__all__' mirrors the backend's media=None ("all media"): expand it
        // to the currently selected channels so it takes the normal single-/
        // multi-channel path. Any concrete ids alongside it are redundant (the
        // expansion is already a superset). Without a channel list we cannot
        // represent it, so drop it (fail-open).
        if (selectedChannelKeys.length === 0) return
        channelIds = selectedChannelKeys
      }
      channelIds = channelIds.filter(function (id) { return !!id })
      if (channelIds.length === 0) return
      var interval = typeToInterval(r.type, Number(r.firstValue), Number(r.secondValue))
      if (!interval) return
      var periodIndices = null
      if (r.timespan === 'custom_range') {
        var from = periods.indexOf(r.fromDate)
        var to = periods.indexOf(r.toDate)
        if (from === -1 || to === -1) return
        if (from > to) { var swap = from; from = to; to = swap }
        periodIndices = []
        for (var i = from; i <= to; i++) periodIndices.push(i)
      }
      constraints.push({ channels: channelIds, periodIndices: periodIndices, lower: interval.lower, upper: interval.upper })
    })
  } else {
    var restrictionValues = params.restrictionValues || {}
    Object.keys(restrictionValues).forEach(function (channel) {
      var r = restrictionValues[channel]
      if (!r || r.type === 'unrestricted') return
      var interval = typeToInterval(r.type, Number(r.firstValue), Number(r.secondValue))
      if (!interval) return
      constraints.push({ channels: [channel], periodIndices: null, lower: interval.lower, upper: interval.upper })
    })
  }
  return constraints
}

// Per-channel decomposition. Returns { feasible, reason } where reason is a
// structured { code, params } so the component owns translation. Diagnostic
// order: per-channel capacity first (most actionable), then the budget.
export function checkFeasibility (params) {
  try {
    var mediaScales = params.mediaScales || {}
    var periods = params.periods || []
    var channels = params.channels || {}
    var constraints = params.constraints || []
    var budget = Number(params.budget)
    var budgetAllocation = params.budgetAllocation || 'equality'

    var periodCount = periods.length
    // Not enough information to decide — fail open.
    if (!isFinite(budget) || budget <= 0 || periodCount === 0) {
      return feasible()
    }

    // The backend rejects an empty media_columns outright ("Media columns
    // empty"), regardless of budget allocation — mirror that instead of
    // guessing capacity from the full model channel list.
    var channelKeys = Object.keys(channels)
    if (channelKeys.length === 0) {
      return infeasible('noChannelsSelected', {})
    }

    // Mirror of the backend's validate_media_in_constraints: a constraint
    // referencing a channel outside media_columns makes the backend error out,
    // so the plan could never run. Any bound type counts (also upper-only) —
    // the backend rejects the constraint by reference, not by its values.
    var selectedSet = {}
    channelKeys.forEach(function (key) { selectedSet[key] = true })
    for (var vi = 0; vi < constraints.length; vi++) {
      var vc = constraints[vi]
      if (!vc.channels) continue
      for (var vj = 0; vj < vc.channels.length; vj++) {
        if (selectedSet[vc.channels[vj]] !== true) {
          return infeasible('restrictionOnUnselectedChannel', { channels: [vc.channels[vj]] })
        }
      }
    }

    // Single-channel constraints feed the exact per-channel decomposition;
    // multi-channel ones only get the necessary-condition checks below.
    var singleChannelConstraints = []
    var multiChannelConstraints = []
    constraints.forEach(function (c) {
      if (!c.channels || c.channels.length === 0) return
      if (c.channels.length === 1) {
        singleChannelConstraints.push({ channel: c.channels[0], periodIndices: c.periodIndices, lower: c.lower, upper: c.upper })
      } else {
        multiChannelConstraints.push(c)
      }
    })

    var byChannel = {}
    singleChannelConstraints.forEach(function (c) {
      if (!byChannel[c.channel]) byChannel[c.channel] = []
      byChannel[c.channel].push(c)
    })

    var totalMin = 0
    var totalMax = 0
    // Per selected channel: the maximum total spend channelSpendRange allows
    // (its single-channel uppers already folded in, so it is the same number
    // totalMax is built from) and its raw per-period cap. The multi-channel
    // upper reduction is measured against these two, which is what makes it
    // subtractable from totalMax — see effectiveMax.
    var activeChannelMax = {}
    var activeChannelCap = {}
    // Cells (channel|periodIdx) whose lower spend is already counted in
    // totalMin. A multi-channel group lower may be ADDED to totalMin only if its
    // box is disjoint from these (and from other added groups) — see the greedy
    // packing below.
    var occupiedBySingles = {}

    for (var ci = 0; ci < channelKeys.length; ci++) {
      var channel = channelKeys[ci]
      var scale = mediaScales[channel]
      // Negative scales are a backend hard error; treat them like a missing
      // scale (fail-open) instead of computing nonsensical negative capacity.
      var cap = (scale === null || scale === undefined || scale < 0) ? Infinity : DIMINISHING_RETURNS_POINT * scale
      var channelConstraints = byChannel[channel] || []

      var range = channelSpendRange(channel, channelConstraints, cap, periodCount)
      if (range.infeasible) {
        return infeasible(range.reason, { channel: channel })
      }
      totalMin += range.min
      totalMax = totalMax + range.max
      activeChannelMax[channel] = range.max
      activeChannelCap[channel] = cap

      // Reserve the cells backing this channel's counted lower. The box is the
      // union of the channel's constrained windows — a valid superset of where
      // that lower spend can live, so a group disjoint from it truly shares no
      // spend with it.
      for (var si = 0; si < channelConstraints.length; si++) {
        if (Math.max(channelConstraints[si].lower || 0, 0) <= 0) continue
        var sIndices = channelConstraints[si].periodIndices || allIndices(periodCount)
        for (var sj = 0; sj < sIndices.length; sj++) {
          occupiedBySingles[channel + '|' + sIndices[sj]] = true
        }
      }
    }

    // Necessary conditions per multi-channel constraint: a violated one proves
    // the LP infeasible; anything else passes (fail-open). Each group lower
    // also bounds total spend from below (its (channels × window) box is a
    // subset of all (m, t)), so the largest one tightens the budget test.
    var maxGroupLower = 0
    // Each item: { lower, cells } — a group's (channels × window) box and its
    // lower bound. Fed to the greedy disjoint-box packing that tightens
    // effectiveMin below.
    var groupItems = []
    // Each item: { reduction, channels } — how much total capacity this group's
    // upper takes away, and over which channels. Fed to the greedy
    // channel-disjoint packing that tightens effectiveMax below.
    var groupUpperItems = []
    for (var mi = 0; mi < multiChannelConstraints.length; mi++) {
      var mc = multiChannelConstraints[mi]
      var mcLower = Math.max(mc.lower || 0, 0)
      var mcUpper = isFinite(mc.upper) ? mc.upper : Infinity
      if (mcUpper < mcLower) {
        return infeasible('groupMinExceedsMax', { channels: mc.channels })
      }
      var mcIndices = mc.periodIndices || allIndices(periodCount)
      var windowSize = mcIndices.length
      // groupCap spans every declared group channel (forced-minimum capacity
      // check).
      var groupCap = 0
      var groupCells = []
      for (var gi = 0; gi < mc.channels.length; gi++) {
        var gName = mc.channels[gi]
        var gScale = mediaScales[gName]
        var gCap = (gScale === null || gScale === undefined || gScale < 0) ? Infinity : DIMINISHING_RETURNS_POINT * gScale
        groupCap = groupCap + mul(windowSize, gCap)
        for (var gj = 0; gj < mcIndices.length; gj++) {
          groupCells.push(gName + '|' + mcIndices[gj])
        }
      }
      // Forced group minimum exceeds what the group's box can physically hold.
      if (isFinite(groupCap) && mcLower > groupCap + tolerance(mcLower)) {
        return infeasible('groupMinExceedsCapacity', { channels: mc.channels })
      }
      if (mcLower > maxGroupLower) maxGroupLower = mcLower
      if (mcLower > 0) groupItems.push({ lower: mcLower, cells: groupCells })
      if (isFinite(mcUpper)) {
        var mcReduction = groupUpperReduction(mc.channels, mcUpper, windowSize, periodCount, activeChannelMax, activeChannelCap)
        if (mcReduction > 0) {
          groupUpperItems.push({ reduction: mcReduction, channels: mc.channels })
        }
      }
    }

    var budgetTolerance = tolerance(budget)
    // Greedy disjoint-box packing: the sum of lowers over pairwise-disjoint
    // (channel, period) boxes is always a valid lower bound on total spend
    // (disjoint boxes cannot share spend), so it never overestimates the true
    // minimum and never falsely blocks. Seed with the single-channel cells
    // already counted in totalMin, then add each group whose box is disjoint
    // from everything picked so far (largest lower first). Groups that overlap a
    // single-channel box or an already-picked group are skipped — their lower
    // could share spend, so summing would be unsound.
    groupItems.sort(function (a, b) { return b.lower - a.lower })
    var occupied = Object.assign({}, occupiedBySingles)
    var packedGroupLowerSum = 0
    for (var pi = 0; pi < groupItems.length; pi++) {
      var item = groupItems[pi]
      var conflict = false
      for (var pc = 0; pc < item.cells.length; pc++) {
        if (occupied[item.cells[pc]]) { conflict = true; break }
      }
      if (conflict) continue
      packedGroupLowerSum += item.lower
      for (var po = 0; po < item.cells.length; po++) occupied[item.cells[po]] = true
    }
    // totalMin + packedGroupLowerSum is a valid lower bound (disjoint boxes) and
    // is >= totalMin. maxGroupLower separately bounds the total when a single
    // large group overlaps the single-channel cells (so it is not in the
    // packing but still lower-bounds total spend on its own). The max of two
    // valid lower bounds is valid.
    var effectiveMin = Math.max(totalMin + packedGroupLowerSum, maxGroupLower)
    if (effectiveMin > budget + budgetTolerance) {
      return infeasible('sumOfMinimumsExceedsBudget', {})
    }
    // Greedy channel-disjoint packing of the group-upper reductions. Each
    // reduction bounds the TOTAL spend of that group's channels (inside the
    // window plus their capacity outside it), so two groups sharing a channel
    // would count that channel twice: only reductions over disjoint channel
    // sets are summed, largest first. Summing over disjoint sets stays a valid
    // ceiling, so this never falsely blocks.
    groupUpperItems.sort(function (a, b) { return b.reduction - a.reduction })
    var reducedChannels = {}
    var packedGroupUpperReduction = 0
    for (var ui = 0; ui < groupUpperItems.length; ui++) {
      var uItem = groupUpperItems[ui]
      var uConflict = false
      for (var uc = 0; uc < uItem.channels.length; uc++) {
        if (reducedChannels[uItem.channels[uc]]) { uConflict = true; break }
      }
      if (uConflict) continue
      packedGroupUpperReduction += uItem.reduction
      for (var uo = 0; uo < uItem.channels.length; uo++) reducedChannels[uItem.channels[uo]] = true
    }
    // totalMax sums each channel's own achievable maximum, which folds in the
    // single-channel uppers but knows nothing about the group ones; the packed
    // reduction is exactly the capacity those group uppers take away from that
    // same base. Applying both to one ceiling is the point: a plan capped by
    // single-channel uppers AND by a group upper at the same time is bounded by
    // neither alone. With full-period windows and channel-disjoint groups this
    // is not merely a bound — it is the LP's exact maximum total spend. The
    // cover bound comes from the same uppers by another route, so the smaller
    // of the two valid ceilings is used.
    var effectiveMax = Math.min(
      totalMax - packedGroupUpperReduction,
      coverCeiling(constraints, channelKeys, activeChannelCap, periodCount)
    )
    if (budgetAllocation === 'equality' && isFinite(effectiveMax) && budget > effectiveMax + budgetTolerance) {
      return infeasible('budgetExceedsMaxCapacity', {})
    }
    return feasible()
  } catch (err) {
    return feasible()
  }
}

// Achievable [min, max] total spend for one channel under its per-period cap and
// its own window-sum constraints. The closed form of the disjoint branch is
// exact; when any two windows overlap the decomposition is invalid, so the
// overlap branch falls back to relaxed-but-sound bounds instead of dropping the
// channel's constraints.
function channelSpendRange (channel, channelConstraints, cap, periodCount) {
  if (channelConstraints.length === 0) {
    return { min: 0, max: mul(periodCount, cap), infeasible: false }
  }

  // Normalize once. Windows built by buildClientConstraints are contiguous
  // index ranges (full period or from..to), which the nested-window check in
  // the overlap branch relies on.
  var items = channelConstraints.map(function (c) {
    var indices = c.periodIndices || allIndices(periodCount)
    return {
      indices: indices,
      windowSize: indices.length,
      lower: Math.max(c.lower || 0, 0),
      upper: isFinite(c.upper) ? c.upper : Infinity
    }
  })

  var i, j
  // Per-constraint checks, valid regardless of window overlap.
  for (i = 0; i < items.length; i++) {
    // Invalid interval: forced minimum above the allowed maximum (e.g. a
    // "between" with min > max). Nothing else catches this for budget-mode
    // restrictions, so the feasibility check owns it.
    if (items[i].upper < items[i].lower) {
      return { min: 0, max: 0, infeasible: true, reason: 'channelMinExceedsMax' }
    }
    // Forced minimum exceeds what this window can physically hold.
    var itemWindowCap = mul(items[i].windowSize, cap)
    if (isFinite(itemWindowCap) && items[i].lower > itemWindowCap + tolerance(items[i].lower)) {
      return { min: 0, max: 0, infeasible: true, reason: 'channelMinExceedsCapacity' }
    }
  }

  var covered = new Array(periodCount)
  var overlap = false
  for (i = 0; i < items.length && !overlap; i++) {
    var indices = items[i].indices
    for (j = 0; j < indices.length; j++) {
      if (covered[indices[j]]) { overlap = true; break }
      covered[indices[j]] = true
    }
  }

  if (overlap) {
    // The exact decomposition is invalid, but sound bounds remain:
    // - Nested windows: A inside B forces sum(A) <= sum(B) <= upper(B), so a
    //   lower(A) above upper(B) is a direct contradiction. Windows are
    //   contiguous, so containment reduces to endpoint comparison.
    // - Any window lower also bounds the channel TOTAL from below (spend is
    //   non-negative), so the largest one is a valid channel minimum.
    // - Any window upper caps the total at upper + capacity outside its
    //   window; the tightest one is a valid channel maximum.
    for (i = 0; i < items.length; i++) {
      for (j = 0; j < items.length; j++) {
        if (i === j) continue
        var inner = items[i]
        var outer = items[j]
        var contained = inner.indices[0] >= outer.indices[0] &&
          inner.indices[inner.indices.length - 1] <= outer.indices[outer.indices.length - 1]
        if (contained && inner.lower > outer.upper + tolerance(inner.lower)) {
          return { min: 0, max: 0, infeasible: true, reason: 'channelConflictingRestrictions' }
        }
      }
    }
    var relaxedMin = 0
    var relaxedMax = mul(periodCount, cap)
    for (i = 0; i < items.length; i++) {
      if (items[i].lower > relaxedMin) relaxedMin = items[i].lower
      if (isFinite(items[i].upper)) {
        var itemMax = items[i].upper + mul(periodCount - items[i].windowSize, cap)
        if (itemMax < relaxedMax) relaxedMax = itemMax
      }
    }
    // Both bounds are valid, so min above max means partially-overlapping
    // windows contradict each other even without containment.
    if (relaxedMin > relaxedMax + tolerance(relaxedMin)) {
      return { min: 0, max: 0, infeasible: true, reason: 'channelConflictingRestrictions' }
    }
    return { min: relaxedMin, max: relaxedMax, infeasible: false }
  }

  var channelMin = 0
  var channelMax = 0
  var coveredCount = 0
  for (i = 0; i < items.length; i++) {
    coveredCount += items[i].windowSize
    channelMin += items[i].lower
    channelMax = channelMax + Math.min(items[i].upper, mul(items[i].windowSize, cap))
  }
  var uncovered = periodCount - coveredCount
  if (uncovered > 0) channelMax = channelMax + mul(uncovered, cap)
  return { min: channelMin, max: channelMax, infeasible: false }
}

// Total capacity a group upper takes away from the plan. The members reach at
// most `upper` inside the window plus whatever they can hold OUTSIDE it, where
// the upper does not apply (zero for a full-period group, the common case), so
// anything they could otherwise absorb above that is capacity the plan cannot
// use. Measuring the gap against activeChannelMax — the members' own maxima —
// instead of raw capacity keeps it free of the single-channel uppers totalMax
// already applied, so it can be subtracted from totalMax directly. Only channels
// totalMax counts take part, so it never subtracts capacity outside that base.
function groupUpperReduction (channels, upper, windowSize, periodCount, activeChannelMax, activeChannelCap) {
  var maxSum = 0
  var outsideCapacity = 0
  for (var i = 0; i < channels.length; i++) {
    var name = channels[i]
    if (activeChannelMax[name] === undefined) continue
    maxSum = maxSum + activeChannelMax[name]
    outsideCapacity = outsideCapacity +
      Math.min(mul(periodCount - windowSize, activeChannelCap[name]), activeChannelMax[name])
  }
  if (!isFinite(maxSum) || !isFinite(outsideCapacity)) return 0
  return maxSum - (upper + outsideCapacity)
}

// Ceiling on total spend from covering the (channel, period) grid: every upper
// bounds the spend of its own cells and every other cell is bounded by its
// capacity, so their sum bounds the plan. Spend is non-negative, so boxes that
// overlap only count that spend twice: the ceiling can come out loose, never
// too low, and therefore never falsely blocks. A fully covered grid needs no
// capacity at all, so a channel without a scale does not open the ceiling.
// Boxes are picked greedily by how much they lower it, largest first (ties,
// which are the boxes taking cells of infinite capacity, by lowest upper).
function coverCeiling (constraints, channelKeys, activeChannelCap, periodCount) {
  var boxes = []
  constraints.forEach(function (c) {
    if (!c.channels || !isFinite(c.upper)) return
    var cells = []
    var indices = c.periodIndices || allIndices(periodCount)
    c.channels.forEach(function (name) {
      indices.forEach(function (index) { cells.push({ key: name + '|' + index, cap: activeChannelCap[name] }) })
    })
    boxes.push({ upper: c.upper, cells: cells })
  })

  var covered = {}
  var ceiling = 0
  while (boxes.length > 0) {
    var best = null
    var bestSaving = 0
    for (var i = 0; i < boxes.length; i++) {
      var capacity = 0
      for (var j = 0; j < boxes[i].cells.length; j++) {
        if (!covered[boxes[i].cells[j].key]) capacity = capacity + boxes[i].cells[j].cap
      }
      var saving = capacity - boxes[i].upper
      if (saving > bestSaving || (saving === bestSaving && best && boxes[i].upper < best.upper)) {
        best = boxes[i]
        bestSaving = saving
      }
    }
    if (!best) break
    ceiling += best.upper
    best.cells.forEach(function (cell) { covered[cell.key] = true })
    boxes.splice(boxes.indexOf(best), 1)
  }

  channelKeys.forEach(function (name) {
    for (var index = 0; index < periodCount; index++) {
      if (!covered[name + '|' + index]) ceiling = ceiling + activeChannelCap[name]
    }
  })
  return ceiling
}

function allIndices (count) {
  var arr = []
  for (var i = 0; i < count; i++) arr.push(i)
  return arr
}

// Slack for floating-point noise when comparing spend amounts. Relative, but
// capped below one spend unit: budgets and restrictions are whole units, so on
// large budgets (1e9 * 1e-9 = 1) an uncapped tolerance let restrictions that
// overshoot or undershoot the budget by exactly 1 pass as feasible.
function tolerance (value) {
  return Math.min(0.5, Math.max(1, Math.abs(value)) * 1e-9)
}

// Guards 0 * Infinity (which would be NaN) to 0.
function mul (a, b) {
  if (a === 0 || b === 0) return 0
  return a * b
}

function feasible () {
  return { feasible: true, reason: null }
}

function infeasible (code, reasonParams) {
  return { feasible: false, reason: { code: code, params: reasonParams || {} } }
}
